import { ChangeDetectionStrategy, Component, HostListener, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ConfigurationService } from '@draeger-academy/common/lib/services/configuration/configuration.service';
import { Site } from 'models/site';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PortalConfigurationService, SubscriptionCollector, CoursesService } from '@draeger-academy/common';
import { ResponsiveState } from '@viablelogic/ngx-responsive';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';


@Component({
    selector: 'da-language-country-switcher',
    templateUrl: './language-country-switcher.component.html',
    styleUrls: ['./language-country-switcher.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LanguageCountrySwitcherComponent implements OnInit, OnChanges {
    public flagPath = 'en';
    public flagPathforPortals: string;
    public selectedValue: string;
    public countryCode: string;
    private _$site: BehaviorSubject<Site> = new BehaviorSubject<Site>(null);
    private _$portalLanguages: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$flagPathforPortals: BehaviorSubject<any> = new BehaviorSubject<string>('');
    public isLanguageSelectionOpen$ = new BehaviorSubject(false);
    public showLanguageSelection$ = new BehaviorSubject(false);
    private _$isMobile: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public portalV2 = false;
    public _$currentLang: BehaviorSubject<any> = new BehaviorSubject<string>('');
    public portal: string;
    @SubscriptionCollector() private _subscriptions;

    constructor(
        public _configurationService: ConfigurationService,
        private _router: Router,
        private _responsiveState: ResponsiveState,
        private _portalConfigurationService: PortalConfigurationService,
        private _translateService: TranslateService,
        private _coursesService: CoursesService
    ) {
        this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            this.getSite();
        });
    }

    public get sites$(): Observable<Site> {
        return this._$site.asObservable();
    }

    public get portalLanguages$(): Observable<any> {
        return this._$portalLanguages.asObservable();
    }
    public get currentLang$(): Observable<any> {
        return this._$currentLang.asObservable();
    }

    public get flagPathforPortals$(): Observable<string> {
        return this._$flagPathforPortals.asObservable();
    }
    public get isMobile$(): Observable<boolean> {
        return this._$isMobile.asObservable();
    }

    @HostListener('window:resize')
    resizeBy() {
        this._resizeHandler();
    }

    public ngOnInit(): void {
        this.getSite();
        this._resizeHandler();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.getSite();
    }

    getSite() {
        const _isPortal = this._configurationService.isPartnerPage();
        if (!_isPortal) {
            const _site = this._configurationService.getSiteFromUrl();
            if (_site) {
                this.flagPath = '/assets/images/flags/' + _site.countryCode.toLowerCase() + '.png';
                this.selectedValue = _site.site.toLowerCase();
                this._$site.next(_site);
                if (this._$site.getValue().languages && this._$site.getValue().languages.length > 1) {
                    this.showLanguageSelection$.next(true);
                } else {
                    this.showLanguageSelection$.next(false);
                }
            }
        } else {
            this.getPortaLanguage();
            this._configurationService.getPortalVersion().subscribe((pv) => {
                if (pv === 'v2') {
                    this.portalV2 = true;
                    this._configurationService.portalPreferences$.subscribe((preferences) => {
                        if (preferences) {
                            this.selectedValue = preferences.defaultLanguage.toLowerCase();
                            this._$flagPathforPortals.next('/assets/images/flags/' + preferences.defaultCountry.toLowerCase() + '.png');
                            if (!preferences.defaultCountry || preferences.defaultCountry === '' || preferences.defaultCountry === null) {
                                this._$flagPathforPortals.next('/assets/images/flags/ww.png');
                            }
                        }
                        if (preferences && preferences.availableLanguages.length > 1 && this._$portalLanguages.getValue() === null) {
                            this._$portalLanguages.next(preferences.availableLanguages);
                        }
                    });
                }
            });
        }
    }
    public toggleLanguageSelectionButton(): void {
        this.isLanguageSelectionOpen$.next(!this.isLanguageSelectionOpen$.getValue());
    }

    public closeLanguageSelectionButton(): void {
        this.isLanguageSelectionOpen$.next(false);
    }

    private _resizeHandler(): void {
        this._subscriptions.push(
            this._responsiveState.elemento$.subscribe((val) => {
                const _showMobile = val === 'xs' || val === 'sm';
                this._$isMobile.next(_showMobile);
            })
        );
    }

    changeLanguageOfPortal(lang) {
        this._$currentLang.next(lang.name);
        this._configurationService.triggerLanguageChangeAndReloadAll(lang.name.toLowerCase(), this._configurationService.isPartnerPage());
        this._configurationService.currentPortal$.subscribe((portalId) => {
            if (portalId) {
                const obj = { portalId: portalId, lang: lang.name.toLowerCase() };
                sessionStorage.setItem('portalLang', JSON.stringify(obj));
            }
        });
        const portalLang = lang.name.toLowerCase();
        this.countryCode = JSON.parse(localStorage.getItem('portalCountryCode')).countryCode;
        this._coursesService.getCategories(this.portal, this.countryCode).then((categories) => {
            this._coursesService.languageSelected.emit(portalLang);
            this.closeLanguageSelectionButton();
        });
    }

    getPortaLanguage() {
        this._configurationService.currentPortal$.subscribe((portalId) => {
            if (portalId) {
                this._portalConfigurationService.loadPortalPreferences(portalId).then((preferences) => {
                    const lang = this._portalConfigurationService.getPortalLanguage(preferences);
                    this._$currentLang.next(lang.toUpperCase());
                    this.portal = portalId;
                });
                this._configurationService.triggerLanguageChangeAndReloadAll(
                    this._$currentLang.getValue(),
                    this._configurationService.isPartnerPage()
                );
            }
        });
    }
}
